import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@chakra-ui/react";
import TransactionList from "./components/TransactionList";
import { Controller } from "../../../Controller/Controller";
import { Alert } from "../../../components/Alert/Alert";
import { Popconfirm, Button } from "antd";

const DEFAULT_PAGE_SIZE = 10;

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
};

const Transaction = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });

  const fetchTransactions = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const response = await Controller.GetOfficeTransactions(
          page,
          pagination.pageSize
        );

        if (response.status < 300) {
          const payload = response.json || {};
          setTransactions(payload.results || []);
          setPagination((prev) => ({
            ...prev,
            current: page,
            total: payload.count ?? payload.results?.length ?? 0,
          }));
        } else {
          Alert.openNotification("Failed to load transactions", "error");
        }
      } catch (error) {
        console.error("Failed to fetch transactions", error);
        Alert.openNotification("Failed to load transactions", "error");
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  useEffect(() => {
    fetchTransactions(1);
  }, [fetchTransactions]);

  const handleRefund = useCallback(
    async (row) => {
      if (!row?.transfer_id) {
        Alert.openNotification(
          "No payment identifier (transfer_id) found.",
          "error"
        );
        return;
      }

      const officeId = localStorage.getItem("selectedOffice");
      if (!officeId) {
        Alert.openNotification("Office id not found.", "error");
        return;
      }

      // اصلاح شرط: چک کردن وضعیت paid یا succeeded
      const status = row.status ? String(row.status).toLowerCase().trim() : "";
      if (status && status !== "succeeded" && status !== "paid") {
        Alert.openNotification(
          "Only succeeded or paid payments can be refunded.",
          "error"
        );
        return;
      }

      setIsRefunding(true);

      try {
        const response = await Controller.refundPayment(
          officeId,
          true,
          row.transfer_id
        );

        if (response.status < 300) {
          Alert.openNotification("Payment refunded successfully.", "success");
          await fetchTransactions(pagination.current || 1);
        } else {
          const errorMessage =
            (response.json &&
              (response.json.detail || response.json.message)) ||
            response.message ||
            "Failed to refund payment.";
          Alert.openNotification(errorMessage, "error");
        }
      } catch (error) {
        console.error("Failed to refund payment", error);
        Alert.openNotification("Failed to refund payment.", "error");
      } finally {
        setIsRefunding(false);
      }
    },
    [fetchTransactions, pagination.current]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Member",
        key: "member",
        render: (row) => {
          if (!row.source_client) return "-";

          const raw = String(row.source_client).trim();
          const nameOnly = raw.split("--")[0].trim();
          const formatted = nameOnly.replace(/([a-z])([A-Z])/g, "$1 $2");

          return formatted || "-";
        },
      },
      {
        Header: "Amount",
        key: "amount",
        render: (row) =>
          row.amount
            ? `${row.amount} ${
                row.currency ? row.currency.toUpperCase() : ""
              }`.trim()
            : "-",
      },
      {
        Header: "Currency",
        key: "currency",
        render: (row) =>
          row.currency && String(row.currency).trim() !== ""
            ? String(row.currency).toUpperCase()
            : "-",
      },
      {
        Header: "Date",
        key: "created",
        render: (row) => formatDateTime(row.created),
      },
      {
        Header: "Description",
        accessor: "description",
      },
      {
        Header: "Status",
        accessor: "status",
      },
      {
        Header: "Action",
        key: "action",
        render: (row) => {
          const status = row.status
            ? String(row.status).toLowerCase().trim()
            : "";

          // اصلاح شرط: اینجا هم paid را اضافه کردیم
          const canRefund =
            !!row.transfer_id &&
            (!status || status === "succeeded" || status === "paid");

          return (
            <Popconfirm
              title="Refund payment"
              description="Are you sure you want to refund this payment?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleRefund(row)}
              disabled={!canRefund || isRefunding}
            >
              <Button
                type="link"
                danger
                style={{ padding: 0 }}
                disabled={!canRefund || isRefunding}
                loading={isRefunding}
              >
                Refund
              </Button>
            </Popconfirm>
          );
        },
      },
    ],
    [handleRefund, isRefunding]
  );

  const handlePageChange = (page) => {
    fetchTransactions(page);
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <TransactionList
        title="Payments"
        columnsData={columns}
        tableData={transactions}
        pagination={pagination}
        loading={loading}
        onPageChange={handlePageChange}
      />
    </Box>
  );
};

export default Transaction;
